import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Bot,
  ChartBar,
  Check,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  Clock,
  Contact,
  Flag,
  Inbox as InboxIcon,
  Layers,
  Settings as SettingsIcon,
  Star,
  Tag,
  User,
  Users,
  Zap,
} from 'lucide-react';
import { VIEWS, type InboxView } from '../lib/inboxViews';

// Collapsible left sidebar — the 'new' UI mode only (see lib/uiMode.ts). It is a
// pure re-housing of controls the classic chrome already owns: the scope tabs
// and the horizontal view-tab bar. No filter or query semantics live here; every
// row just calls a setter InboxPage already had, so the two layouts can't drift.
//
// RTL: bekesher runs Hebrew (i18n sets dir=rtl on the document), so this uses
// logical properties — border-e / ms-auto, not border-r / ml-auto.

type Icon = typeof InboxIcon;

// Queue rows, in sidebar order, mapped onto the existing view keys. Unlike the
// classic tab bar (which hides the flagged/parked tabs outside the advanced
// density), the sidebar lists every queue — it's a vertical list with room to
// spare, which is the point of the layout.
const QUEUES: Array<{ key: InboxView; label: string; Icon: Icon; cls?: string }> = [
  { key: 'waiting', label: 'Pending', Icon: InboxIcon },
  { key: 'progress', label: 'In progress', Icon: Clock },
  { key: 'flagged', label: 'AI flagged', Icon: Flag, cls: 'text-amber-600' },
  { key: 'ai', label: 'AI handling', Icon: Bot },
  { key: 'wait_customer', label: 'Waiting (customer)', Icon: User },
  { key: 'wait_internal', label: 'Waiting (internal)', Icon: Users },
  { key: 'resolved', label: 'Resolved', Icon: Check },
  { key: 'all', label: 'All', Icon: Layers },
];

// Reuse the tab bar's hover hints verbatim so both layouts describe a queue the
// same way.
const HINT: Record<string, string> = Object.fromEntries(VIEWS.map((v) => [v.key, v.hint]));

export interface InboxRailProps {
  view: InboxView;
  onView: (v: InboxView) => void;
  scope: 'mine' | 'all';
  onScope: (s: 'mine' | 'all') => void;
  starredOnly: boolean;
  onStarred: (v: boolean) => void;
  /** Live per-view badge counts. Missing or 0 renders no badge. */
  counts: Partial<Record<InboxView, number>>;
  /** How many label filters are active (badge on the Labels row). */
  labelCount: number;
  /** Opens the Labels menu that already lives in the filter row. */
  onOpenLabels: () => void;
  /** True when a team or agent filter is applied. */
  teamActive: boolean;
  /** Focuses the team/agent select in the filter row. */
  onOpenTeams: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  /** "Needs you now" = the Pending queue, unfiltered. Highlighted only when
   *  that exact combination is what's on screen. */
  needsYouNow: boolean;
  onNeedsYouNow: () => void;
}

export function InboxRail({
  view,
  onView,
  scope,
  onScope,
  starredOnly,
  onStarred,
  counts,
  labelCount,
  onOpenLabels,
  teamActive,
  onOpenTeams,
  collapsed,
  onToggleCollapse,
  needsYouNow,
  onNeedsYouNow,
}: InboxRailProps) {
  return (
    <aside
      data-testid="inbox-rail"
      className="hidden md:flex flex-col min-h-0 border-e border-gray-200 bg-gray-50 py-2 px-1.5 gap-0.5 overflow-y-auto"
    >
      <button
        type="button"
        onClick={onToggleCollapse}
        className={`flex items-center gap-2 px-2 py-1 rounded text-gray-500 hover:bg-gray-200/70 ${
          collapsed ? 'justify-center' : ''
        }`}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        // Explicit: expanded, the visible "Collapse" text would otherwise be the
        // accessible name, so the two states wouldn't read as one control.
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-expanded={!collapsed}
      >
        {collapsed ? (
          <ChevronsRight className="w-4 h-4 flex-shrink-0" />
        ) : (
          <>
            <ChevronsLeft className="w-4 h-4 flex-shrink-0" />
            <span className="text-[11px]">Collapse</span>
          </>
        )}
      </button>

      <Section label="Focus" collapsed={collapsed} />
      <Row
        Icon={Zap}
        label="Needs you now"
        count={counts.waiting}
        active={needsYouNow}
        collapsed={collapsed}
        onClick={onNeedsYouNow}
        title="Escalated conversations waiting for an agent — all of them, unfiltered"
      />
      <Row
        Icon={User}
        label="Assigned to me"
        active={scope === 'mine'}
        collapsed={collapsed}
        onClick={() => onScope(scope === 'mine' ? 'all' : 'mine')}
        title="Only conversations assigned to you"
      />
      <Row
        Icon={Star}
        label="Starred"
        active={starredOnly}
        collapsed={collapsed}
        onClick={() => onStarred(!starredOnly)}
        title="Only starred conversations"
      />

      <Section label="Queues" collapsed={collapsed} />
      {QUEUES.map((q) => (
        <Row
          key={q.key}
          Icon={q.Icon}
          label={q.label}
          // Same rule as the classic tab bar: no badge on Resolved (none needed)
          // or All (its count listener excludes resolved/snoozed, so it would
          // undercount what the All view actually shows).
          count={q.key === 'resolved' || q.key === 'all' ? undefined : counts[q.key]}
          active={view === q.key}
          collapsed={collapsed}
          onClick={() => onView(q.key)}
          title={HINT[q.key]}
          cls={q.cls}
        />
      ))}

      <Section label="Filters" collapsed={collapsed} />
      <Row
        Icon={Tag}
        label="Labels"
        count={labelCount || undefined}
        active={labelCount > 0}
        collapsed={collapsed}
        onClick={onOpenLabels}
        title="Filter by label"
        trailing={<ChevronDown className="w-3 h-3" />}
      />
      <Row
        Icon={Users}
        label="Teams"
        active={teamActive}
        collapsed={collapsed}
        onClick={onOpenTeams}
        title="Filter by team or agent"
        trailing={<ChevronDown className="w-3 h-3" />}
      />

      <div className="mt-auto pt-2 border-t border-gray-200 flex gap-1 justify-center flex-wrap">
        <RailLink to="/contacts" label="Contacts" Icon={Contact} />
        <RailLink to="/reports" label="Reports" Icon={ChartBar} />
        <RailLink to="/settings" label="Settings" Icon={SettingsIcon} />
      </div>
    </aside>
  );
}

function Section({ label, collapsed }: { label: string; collapsed: boolean }) {
  // Collapsed to icons: the heading text has nowhere to go, so the group
  // boundary becomes a rule instead.
  if (collapsed) return <div className="my-1 mx-2 border-t border-gray-200" />;
  return (
    <div className="px-2 pt-2.5 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
      {label}
    </div>
  );
}

function Row({
  Icon,
  label,
  count,
  active,
  collapsed,
  onClick,
  title,
  cls,
  trailing,
}: {
  Icon: Icon;
  label: string;
  count?: number;
  active?: boolean;
  collapsed: boolean;
  onClick: () => void;
  title?: string;
  cls?: string;
  trailing?: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      // Collapsed rows are icon-only, so the label has to live in the tooltip or
      // it's unreachable without expanding.
      title={collapsed ? `${label}${title ? ` — ${title}` : ''}` : title}
      aria-label={label}
      aria-pressed={!!active}
      className={`flex items-center gap-2 rounded px-2 py-1.5 text-xs transition-colors ${
        collapsed ? 'justify-center' : ''
      } ${
        active
          ? 'bg-primary-50 text-primary-700 font-medium'
          : `${cls ?? 'text-gray-600'} hover:bg-gray-200/70`
      }`}
    >
      <Icon className="w-4 h-4 flex-shrink-0" />
      {!collapsed && <span className="truncate">{label}</span>}
      {!collapsed && count != null && count > 0 && (
        <span
          className={`ms-auto text-[9px] font-semibold rounded-full px-1.5 py-0.5 min-w-[16px] text-center ${
            active ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-500'
          }`}
        >
          {count}
        </span>
      )}
      {!collapsed && trailing && !(count != null && count > 0) && (
        <span className="ms-auto">{trailing}</span>
      )}
    </button>
  );
}

function RailLink({ to, label, Icon }: { to: string; label: string; Icon: Icon }) {
  return (
    <Link
      to={to}
      title={label}
      aria-label={label}
      className="p-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-200/70"
    >
      <Icon className="w-4 h-4" />
    </Link>
  );
}
